import {
  ChevronDown,
  Database,
  DollarSign,
  Eye,
  List,
  Network,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OVERLAY_CATEGORIES, OVERLAY_LABELS, type OverlayCategory } from "@/lib/arch-overlay";
import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";

const ICONS: Record<OverlayCategory, typeof DollarSign> = {
  cost_impact: DollarSign,
  scale_performance: TrendingUp,
  security: ShieldAlert,
  compliance: ShieldCheck,
  data_readiness: Database,
  data_connectivity: Network,
};

// Overlay category dots use the dedicated --risk-* taxonomy tokens (spec §1.1)
// for the four risk overlays; the two data overlays borrow the datastore /
// gateway node tokens so everything swaps with the theme.
const DOT_CLASS: Record<OverlayCategory, string> = {
  cost_impact: "bg-risk-cost",
  scale_performance: "bg-risk-performance",
  security: "bg-risk-security",
  compliance: "bg-risk-compliance",
  data_readiness: "bg-[var(--node-datastore-border)]",
  data_connectivity: "bg-[var(--node-gateway-border)]",
};

export function OverlayToggle() {
  const { state, dispatch } = useArchViz();
  const active = state.overlay !== "none" ? (state.overlay as OverlayCategory) : null;
  const ActiveIcon = active ? ICONS[active] : Sparkles;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={active ? "default" : "outline"} size="sm">
          <ActiveIcon className="h-3.5 w-3.5 mr-1.5" />
          {active ? OVERLAY_LABELS[active] : "Risks"}
          {active && (
            <span className={cn("ml-1.5 h-2 w-2 rounded-full", DOT_CLASS[active])} aria-hidden />
          )}
          <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuCheckboxItem
          checked={state.showRisks}
          onCheckedChange={() => dispatch({ type: "TOGGLE_RISKS" })}
        >
          <Eye className="h-3.5 w-3.5" />
          <span>Show risk badges on nodes</span>
        </DropdownMenuCheckboxItem>
        <DropdownMenuItem onSelect={() => dispatch({ type: "OPEN_RISKS_SHEET" })}>
          <List className="h-3.5 w-3.5" />
          View all risks…
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Highlight by category
        </DropdownMenuLabel>
        {OVERLAY_CATEGORIES.map((mode) => {
          const Icon = ICONS[mode];
          return (
            <DropdownMenuCheckboxItem
              key={mode}
              checked={state.overlay === mode}
              onCheckedChange={() =>
                dispatch({
                  type: "SET_OVERLAY",
                  overlay: state.overlay === mode ? "none" : mode,
                })
              }
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{OVERLAY_LABELS[mode]}</span>
              <span className={cn("ml-auto h-2 w-2 rounded-full", DOT_CLASS[mode])} aria-hidden />
            </DropdownMenuCheckboxItem>
          );
        })}
        {active && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => dispatch({ type: "SET_OVERLAY", overlay: "none" })}>
              <X className="h-3.5 w-3.5" />
              Clear risk highlight
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
